import { AREA, DISTRICT, SECTOR, SUBUNIT } from './schema';

/**
 * Stable FOC AOR ids and hierarchy metadata (no geometries). Geometry is loaded
 * separately in `uscgFocAors` so non-FOC code paths stay light.
 */

export const USCG_FOC_AOR_IDS = {
  atlanticArea: '6a1f1a62-0f1e-4c33-9c0b-1f0b2d3e4a51',
  pacificArea: '6a1f1a62-0f1e-4c33-9c0b-1f0b2d3e4a52',
  district1: '7b2e2b73-1a2f-4d44-8d1c-2a1c3e4f5b61',
  eastDistrict: '7b2e2b73-1a2f-4d44-8d1c-2a1c3e4f5b62',
  greatLakesDistrict: '7b2e2b73-1a2f-4d44-8d1c-2a1c3e4f5b63',
  heartlandDistrict: '7b2e2b73-1a2f-4d44-8d1c-2a1c3e4f5b64',
  southeastDistrict: '7b2e2b73-1a2f-4d44-8d1c-2a1c3e4f5b65',
  arcticDistrict: '7b2e2b73-1a2f-4d44-8d1c-2a1c3e4f5b66',
  northwestDistrict: '7b2e2b73-1a2f-4d44-8d1c-2a1c3e4f5b67',
  oceaniaDistrict: '7b2e2b73-1a2f-4d44-8d1c-2a1c3e4f5b68',
  southwestDistrict: '7b2e2b73-1a2f-4d44-8d1c-2a1c3e4f5b69',
  sectorNewYork: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6c71',
  sectorDelawareBay: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6c72',
  sectorMarylandNcr: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6c73',
  sectorNorthCarolina: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6c74',
  sectorVirginia: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6c75',
  stationNewYork: '9d4a4d95-3c4b-4f66-8f3e-4c3e5a6b7d81',
  stationMontauk: '9d4a4d95-3c4b-4f66-8f3e-4c3e5a6b7d82',
  stationCapeCharles: '9d4a4d95-3c4b-4f66-8f3e-4c3e5a6b7d83',
  stationChincoteague: '9d4a4d95-3c4b-4f66-8f3e-4c3e5a6b7d84',
  stationLittleCreek: '9d4a4d95-3c4b-4f66-8f3e-4c3e5a6b7d85',
  stationMilfordHaven: '9d4a4d95-3c4b-4f66-8f3e-4c3e5a6b7d86',
  stationPortsmouth: '9d4a4d95-3c4b-4f66-8f3e-4c3e5a6b7d87',
  stationWachapreague: '9d4a4d95-3c4b-4f66-8f3e-4c3e5a6b7d88',
} as const;

export function focNameKey(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

export type UscgFocAorMeta = {
  id: string;
  name: string;
  description: string;
  parentId: string | null;
  levelName: typeof AREA | typeof DISTRICT | typeof SECTOR | typeof SUBUNIT;
  levelOrder: number;
  headquarters: string;
  color: string | null;
  /** Present only for boat-station subunits (demo rectangles). */
  bbox?: [number, number, number, number];
};

const AREA_DEFS: UscgFocAorMeta[] = [
  {
    id: USCG_FOC_AOR_IDS.atlanticArea,
    name: 'Atlantic Area',
    description:
      'Coast Guard Atlantic Area directs operations from the Rocky Mountains to the Arabian Gulf, spanning five districts and the Atlantic, Gulf of Mexico, and Great Lakes coasts.',
    parentId: null,
    levelName: AREA,
    levelOrder: 1,
    headquarters: 'Portsmouth, VA',
    color: '#1f6feb',
  },
  {
    id: USCG_FOC_AOR_IDS.pacificArea,
    name: 'Pacific Area',
    description:
      'Coast Guard Pacific Area directs operations across the Pacific from the Rocky Mountains to the waters off East Africa, including Alaska and Hawaii.',
    parentId: null,
    levelName: AREA,
    levelOrder: 1,
    headquarters: 'Alameda, CA',
    color: '#2da44e',
  },
];

const DISTRICT_DEFS: UscgFocAorMeta[] = [
  {
    id: USCG_FOC_AOR_IDS.eastDistrict,
    name: 'East District',
    description:
      'East District covers the Mid-Atlantic coast from New Jersey to North Carolina, including Delaware Bay, the Chesapeake Bay, and the National Capital Region.',
    parentId: USCG_FOC_AOR_IDS.atlanticArea,
    levelName: DISTRICT,
    levelOrder: 2,
    headquarters: 'Portsmouth, VA',
    color: null,
  },
  {
    id: USCG_FOC_AOR_IDS.greatLakesDistrict,
    name: 'Great Lakes District',
    description:
      'Great Lakes District covers the five Great Lakes, the St. Lawrence Seaway, and their connecting rivers along the Canadian border.',
    parentId: USCG_FOC_AOR_IDS.atlanticArea,
    levelName: DISTRICT,
    levelOrder: 2,
    headquarters: 'Cleveland, OH',
    color: null,
  },
  {
    id: USCG_FOC_AOR_IDS.heartlandDistrict,
    name: 'Heartland District',
    description:
      'Heartland District covers the western and central Gulf Coast and the inland river system of the Mississippi, Ohio, Missouri, and Tennessee Rivers.',
    parentId: USCG_FOC_AOR_IDS.atlanticArea,
    levelName: DISTRICT,
    levelOrder: 2,
    headquarters: 'New Orleans, LA',
    color: null,
  },
  {
    id: USCG_FOC_AOR_IDS.district1,
    name: 'Northeast District',
    description:
      'Northeast District covers New England and New York from the Canadian border to northern New Jersey, including Sectors Northern New England, Boston, Southeastern New England, Long Island Sound, and New York.',
    parentId: USCG_FOC_AOR_IDS.atlanticArea,
    levelName: DISTRICT,
    levelOrder: 2,
    headquarters: 'Boston, MA',
    color: null,
  },
  {
    id: USCG_FOC_AOR_IDS.southeastDistrict,
    name: 'Southeast District',
    description:
      'Southeast District covers South Carolina, Georgia, Florida, and the eastern Gulf of Mexico, along with the approaches to the Caribbean.',
    parentId: USCG_FOC_AOR_IDS.atlanticArea,
    levelName: DISTRICT,
    levelOrder: 2,
    headquarters: 'Miami, FL',
    color: null,
  },
  {
    id: USCG_FOC_AOR_IDS.arcticDistrict,
    name: 'Arctic District',
    description:
      'Arctic District covers Alaska, the Aleutian Islands, the Bering Sea, and the U.S. Arctic.',
    parentId: USCG_FOC_AOR_IDS.pacificArea,
    levelName: DISTRICT,
    levelOrder: 2,
    headquarters: 'Juneau, AK',
    color: null,
  },
  {
    id: USCG_FOC_AOR_IDS.northwestDistrict,
    name: 'Northwest District',
    description:
      'Northwest District covers Washington, Oregon, Idaho, and Montana, including Puget Sound and the Columbia River bar.',
    parentId: USCG_FOC_AOR_IDS.pacificArea,
    levelName: DISTRICT,
    levelOrder: 2,
    headquarters: 'Seattle, WA',
    color: null,
  },
  {
    id: USCG_FOC_AOR_IDS.oceaniaDistrict,
    name: 'Oceania District',
    description:
      'Oceania District covers Hawaii, Guam, American Samoa, and the U.S. Pacific islands across the central and western Pacific.',
    parentId: USCG_FOC_AOR_IDS.pacificArea,
    levelName: DISTRICT,
    levelOrder: 2,
    headquarters: 'Honolulu, HI',
    color: null,
  },
  {
    id: USCG_FOC_AOR_IDS.southwestDistrict,
    name: 'Southwest District',
    description:
      'Southwest District covers California, Nevada, Utah, and Arizona, including the Ports of Los Angeles, Long Beach, and San Francisco Bay.',
    parentId: USCG_FOC_AOR_IDS.pacificArea,
    levelName: DISTRICT,
    levelOrder: 2,
    headquarters: 'Alameda, CA',
    color: null,
  },
];

const KNOWN_SECTOR_IDS: Record<string, string> = {
  sectornewyork: USCG_FOC_AOR_IDS.sectorNewYork,
  sectordelawarebay: USCG_FOC_AOR_IDS.sectorDelawareBay,
  sectormarylandncr: USCG_FOC_AOR_IDS.sectorMarylandNcr,
  sectornorthcarolina: USCG_FOC_AOR_IDS.sectorNorthCarolina,
  sectorvirginia: USCG_FOC_AOR_IDS.sectorVirginia,
};

const EXTRA_SECTOR_IDS: Record<string, string> = {
  sectorboston: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d01',
  sectorcharleston: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d02',
  sectorcolumbiariver: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d03',
  sectorcorpuschristi: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d04',
  sectordetroit: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d05',
  sectoreasterngreatlakes: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d06',
  sectorguam: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d07',
  sectorhonolulu: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d08',
  sectorhoustongalveston: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d09',
  sectorhumboldtbay: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d0a',
  sectorjacksonville: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d0b',
  sectorkeywest: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d0c',
  sectorlakemichigan: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d0d',
  sectorlongislandsound: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d0e',
  sectorlosangeleslongbeach: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d0f',
  sectorlowermississippirivermemphis: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d10',
  sectormiami: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d11',
  sectormobile: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d12',
  sectorneworleans: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d13',
  sectornortherngreatlakes: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d14',
  sectornorthernnewenglandportlandmaine: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d15',
  sectorohiovalley: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d16',
  sectorsandiego: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d17',
  sectorsanfrancisco: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d18',
  sectorsanjuan: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d19',
  sectorseaksoutheastalaskajuneau: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d1a',
  sectorseattlepugetsound: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d1b',
  sectorsoutheasternnewenglandprovidence: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d1c',
  sectorstpetersburg: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d1d',
  sectoruppermississippiriverstlouis: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d1e',
  sectorwesternalaskaanchorage: '8c3f3c84-2b3a-4e55-9e2d-3b2d4f5a6d1f',
};

const SECTOR_META: Record<
  string,
  { description: string; headquarters: string }
> = {
  sectordelawarebay: {
    description:
      'Sector Delaware Bay is responsible for the Delaware River and Bay, the Port of Philadelphia, and the coasts of southern New Jersey and Delaware.',
    headquarters: 'Philadelphia, PA',
  },
  sectormarylandncr: {
    description:
      'Sector Maryland - National Capital Region is responsible for the upper Chesapeake Bay, the Port of Baltimore, and the Potomac River through Washington, DC.',
    headquarters: 'Baltimore, MD',
  },
  sectornorthcarolina: {
    description:
      'Sector North Carolina is responsible for the North Carolina coast, the Outer Banks, Cape Hatteras, and the Port of Wilmington.',
    headquarters: 'Wilmington, NC',
  },
  sectorvirginia: {
    description:
      'Sector Virginia is responsible for Hampton Roads, the lower Chesapeake Bay, the Eastern Shore, and the Port of Virginia.',
    headquarters: 'Portsmouth, VA',
  },
  sectornewyork: {
    description:
      'Sector New York is responsible for the Port of New York and New Jersey, the Hudson River, and the approaches to Long Island Sound.',
    headquarters: 'Staten Island, NY',
  },
};

/** ArcGIS sector → district pairs (kept in sync by refreshUscgFocAorGeometry.mjs). */
const SECTOR_CATALOG: Array<{ name: string; districtName: string }> = [
  { name: 'Sector Boston', districtName: 'Northeast District' },
  { name: 'Sector Charleston', districtName: 'Southeast District' },
  { name: 'Sector Columbia River', districtName: 'Northwest District' },
  { name: 'Sector Corpus Christi', districtName: 'Heartland District' },
  { name: 'Sector Delaware Bay', districtName: 'East District' },
  { name: 'Sector Detroit', districtName: 'Great Lakes District' },
  { name: 'Sector Eastern Great Lakes', districtName: 'Great Lakes District' },
  { name: 'Sector Guam', districtName: 'Oceania District' },
  { name: 'Sector Honolulu', districtName: 'Oceania District' },
  { name: 'Sector Houston-Galveston', districtName: 'Heartland District' },
  { name: 'Sector Humboldt Bay', districtName: 'Southwest District' },
  { name: 'Sector Jacksonville', districtName: 'Southeast District' },
  { name: 'Sector Key West', districtName: 'Southeast District' },
  { name: 'Sector Lake Michigan', districtName: 'Great Lakes District' },
  { name: 'Sector Long Island Sound', districtName: 'Northeast District' },
  { name: 'Sector Los Angeles-Long Beach', districtName: 'Southwest District' },
  {
    name: 'Sector Lower Mississippi River (Memphis)',
    districtName: 'Heartland District',
  },
  { name: 'Sector Maryland - NCR', districtName: 'East District' },
  { name: 'Sector Miami', districtName: 'Southeast District' },
  { name: 'Sector Mobile', districtName: 'Heartland District' },
  { name: 'Sector New Orleans', districtName: 'Heartland District' },
  { name: 'Sector New York', districtName: 'Northeast District' },
  { name: 'Sector North Carolina', districtName: 'East District' },
  { name: 'Sector Northern Great Lakes', districtName: 'Great Lakes District' },
  {
    name: 'Sector Northern New England (Portland, Maine)',
    districtName: 'Northeast District',
  },
  { name: 'Sector Ohio Valley', districtName: 'Heartland District' },
  {
    name: 'Sector SEAK - Southeast Alaska (Juneau)',
    districtName: 'Arctic District',
  },
  { name: 'Sector San Diego', districtName: 'Southwest District' },
  { name: 'Sector San Francisco', districtName: 'Southwest District' },
  { name: 'Sector San Juan', districtName: 'Southeast District' },
  { name: 'Sector Seattle (Puget Sound)', districtName: 'Northwest District' },
  {
    name: 'Sector Southeastern New England (Providence)',
    districtName: 'Northeast District',
  },
  { name: 'Sector St. Petersburg', districtName: 'Southeast District' },
  {
    name: 'Sector Upper Mississippi River (St. Louis)',
    districtName: 'Heartland District',
  },
  { name: 'Sector Virginia', districtName: 'East District' },
  {
    name: 'Sector Western Alaska (Anchorage)',
    districtName: 'Arctic District',
  },
];

const SUBUNIT_DEFS: UscgFocAorMeta[] = [
  {
    id: USCG_FOC_AOR_IDS.stationNewYork,
    name: 'Station New York',
    description:
      'Multi-mission small boat station covering New York Harbor and the lower Hudson River.',
    parentId: USCG_FOC_AOR_IDS.sectorNewYork,
    levelName: SUBUNIT,
    levelOrder: 4,
    headquarters: 'Staten Island, NY',
    color: null,
    bbox: [-74.3, 40.45, -73.85, 40.85],
  },
  {
    id: USCG_FOC_AOR_IDS.stationMontauk,
    name: 'Station Montauk',
    description:
      'Small boat station covering the eastern end of Long Island and Block Island Sound.',
    parentId: USCG_FOC_AOR_IDS.sectorNewYork,
    levelName: SUBUNIT,
    levelOrder: 4,
    headquarters: 'Montauk, NY',
    color: null,
    bbox: [-72.3, 40.9, -71.6, 41.3],
  },
  {
    id: USCG_FOC_AOR_IDS.stationCapeCharles,
    name: 'Boat Station Cape Charles',
    description:
      'Small boat station covering the mouth of the Chesapeake Bay and the bayside of the lower Eastern Shore.',
    parentId: USCG_FOC_AOR_IDS.sectorVirginia,
    levelName: SUBUNIT,
    levelOrder: 4,
    headquarters: 'Cape Charles, VA',
    color: null,
    bbox: [-76.15, 37.15, -75.85, 37.4],
  },
  {
    id: USCG_FOC_AOR_IDS.stationChincoteague,
    name: 'Boat Station Chincoteague',
    description:
      'Small boat station covering Chincoteague Inlet, Assateague Island, and the Virginia-Maryland line offshore.',
    parentId: USCG_FOC_AOR_IDS.sectorVirginia,
    levelName: SUBUNIT,
    levelOrder: 4,
    headquarters: 'Chincoteague, VA',
    color: null,
    bbox: [-75.55, 37.75, -75.25, 38.0],
  },
  {
    id: USCG_FOC_AOR_IDS.stationLittleCreek,
    name: 'Boat Station Little Creek',
    description:
      'Multi-mission small boat station covering the Chesapeake Bay Bridge-Tunnel, Lynnhaven Inlet, and the Virginia Beach oceanfront.',
    parentId: USCG_FOC_AOR_IDS.sectorVirginia,
    levelName: SUBUNIT,
    levelOrder: 4,
    headquarters: 'Virginia Beach, VA',
    color: null,
    bbox: [-76.3, 36.85, -76.0, 37.05],
  },
  {
    id: USCG_FOC_AOR_IDS.stationMilfordHaven,
    name: 'Boat Station Milford Haven',
    description:
      'Small boat station covering Mobjack Bay, the Piankatank River, and the western shore of the lower Chesapeake Bay.',
    parentId: USCG_FOC_AOR_IDS.sectorVirginia,
    levelName: SUBUNIT,
    levelOrder: 4,
    headquarters: 'Hudgins, VA',
    color: null,
    bbox: [-76.45, 37.35, -76.15, 37.6],
  },
  {
    id: USCG_FOC_AOR_IDS.stationPortsmouth,
    name: 'Boat Station Portsmouth',
    description:
      'Small boat station covering the Elizabeth River, Norfolk Harbor, and the inner Hampton Roads port.',
    parentId: USCG_FOC_AOR_IDS.sectorVirginia,
    levelName: SUBUNIT,
    levelOrder: 4,
    headquarters: 'Portsmouth, VA',
    color: null,
    bbox: [-76.45, 36.7, -76.2, 36.9],
  },
  {
    id: USCG_FOC_AOR_IDS.stationWachapreague,
    name: 'Boat Station Wachapreague',
    description:
      'Small boat station covering the barrier islands, inlets, and nearshore waters of the seaside Eastern Shore.',
    parentId: USCG_FOC_AOR_IDS.sectorVirginia,
    levelName: SUBUNIT,
    levelOrder: 4,
    headquarters: 'Wachapreague, VA',
    color: null,
    bbox: [-75.8, 37.45, -75.5, 37.75],
  },
];

function sectorIdFor(nameKey: string): string {
  return (
    KNOWN_SECTOR_IDS[nameKey] ??
    EXTRA_SECTOR_IDS[nameKey] ??
    `8c3f3c84-2b3a-4e55-9e2d-${nameKey.slice(0, 12).padEnd(12, '0')}`
  );
}

function buildSectorDefs(): UscgFocAorMeta[] {
  const districtIds = new Map(
    DISTRICT_DEFS.map((def) => [focNameKey(def.name), def.id] as const)
  );
  return SECTOR_CATALOG.map(({ name, districtName }) => {
    const nameKey = focNameKey(name);
    const parentId = districtIds.get(focNameKey(districtName));
    if (!parentId) {
      throw new Error(
        `Missing FOC parent district "${districtName}" for sector ${name}`
      );
    }
    const meta = SECTOR_META[nameKey];
    return {
      id: sectorIdFor(nameKey),
      name,
      description:
        meta?.description ??
        `${name} is a Coast Guard sector under ${districtName}.`,
      parentId,
      levelName: SECTOR,
      levelOrder: 3,
      headquarters: meta?.headquarters ?? districtName,
      color: null,
    };
  });
}

/** Hierarchy metadata for reports / tours (no polygon payload). */
export const USCG_FOC_AOR_DEFS: UscgFocAorMeta[] = [
  ...AREA_DEFS,
  ...DISTRICT_DEFS,
  ...buildSectorDefs(),
  ...SUBUNIT_DEFS,
];
